import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { GraduationCap, Sun, Moon, Menu, X, Mail, Phone, MapPin } from "lucide-react";
import { CONTACT } from "@/marketing/content";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/success-stories", label: "Success Stories" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export function MarketingLayout({ children }) {
  const { user } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background text-foreground noise-overlay">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border">
        <div className="max-w-6xl mx-auto h-16 flex items-center justify-between px-6">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-heading font-extrabold text-lg tracking-tight">EliteHire India</span>
          </Link>

          <nav className="hidden md:flex items-center gap-7">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-sm font-medium transition-colors ${pathname === n.to ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={toggle} className="rounded-md" data-testid="marketing-theme-toggle">
              {theme === "dark" ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
            </Button>
            <Button variant="ghost" className="rounded-md hidden sm:inline-flex" onClick={() => navigate(user ? "/dashboard" : "/auth")} data-testid="nav-signin">
              {user ? "Dashboard" : "Sign In"}
            </Button>
            <Button className="rounded-md hidden sm:inline-flex" onClick={() => navigate("/register")} data-testid="nav-register">
              Register
            </Button>
            <button className="md:hidden p-2" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-label="Menu">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden border-t border-border bg-background px-6 py-4 space-y-3" data-testid="mobile-menu">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block text-sm font-medium text-muted-foreground hover:text-foreground">
                {n.label}
              </Link>
            ))}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="rounded-md flex-1" onClick={() => { setOpen(false); navigate(user ? "/dashboard" : "/auth"); }}>{user ? "Dashboard" : "Sign In"}</Button>
              <Button className="rounded-md flex-1" onClick={() => { setOpen(false); navigate("/register"); }}>Register</Button>
            </div>
          </div>
        )}
      </header>

      <main>{children}</main>

      <footer className="border-t border-border bg-card/40 mt-24">
        <div className="max-w-6xl mx-auto px-6 py-14 grid gap-10 md:grid-cols-4">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-heading font-extrabold text-lg tracking-tight">EliteHire India</span>
            </div>
            <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">
              India's most trusted career launchpad for freshers — resume building, interview coaching and real placement support until you're hired.
            </p>
          </div>
          <div>
            <p className="font-heading font-bold text-sm mb-3">Explore</p>
            <ul className="space-y-2">
              {NAV.map((n) => (
                <li key={n.to}><Link to={n.to} className="text-sm text-muted-foreground hover:text-foreground">{n.label}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-heading font-bold text-sm mb-3">Contact</p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-primary" /> {CONTACT.details[0].value}</li>
              <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-primary" /> {CONTACT.details[1].value}</li>
              <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" /> Hyderabad, India</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-border py-5 text-center text-xs text-muted-foreground font-mono">
          © {new Date().getFullYear()} EliteHire India Pvt. Ltd. · All rights reserved.
        </div>
      </footer>
    </div>
  );
}

export function Section({ children, className = "" }) {
  return <section className={`max-w-6xl mx-auto px-6 ${className}`}>{children}</section>;
}

export function Overline({ children }) {
  return <p className="text-xs font-mono uppercase tracking-[0.2em] text-primary mb-3">{children}</p>;
}
